import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { Subject } from "rxjs";
import { NgxPermissionsService, NgxRolesService } from "ngx-permissions";
import { takeUntil } from "rxjs/operators";

export interface RoleConfig {
  roles?: string | string[] | Record<string, string[]>;
  requireAll?: boolean; // default false = OR logic
  only?: boolean; // default false = allow specified roles + others, true = ONLY specified roles
  allowModules?: string[];
}

@Directive({
  selector: "[appHasAccess]",
})
export class HasAccessDirective implements OnInit, OnDestroy {
  @Input() appHasAccess: RoleConfig;
  private destroy$ = new Subject<void>();

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private permissionsService: NgxPermissionsService
  ) {
    this.permissionsService.permissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateView());
  }

  ngOnInit() {
    this.updateView(); // check permissions when directive is first initialized
  }

  // ngOnChanges(changes: SimpleChanges): void {
  //   if (changes["appHasAccess"]) {
  //     this.updateView();
  //   }
  // }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check a single role (supports NOT logic like "!vendor")
   */
  private async checkRole(role: string): Promise<boolean> {
    const isNegated = role.startsWith("!");
    const roleName = isNegated ? role.substring(1) : role;

    try {
      const hasRole = await this.permissionsService
        .hasPermission(roleName)
        .then((res) => res)
        .catch(() => false);

      return isNegated ? !hasRole : hasRole;
    } catch {
      return isNegated ? true : false;
    }
  }

  /**
   * Check a single permission (supports NOT logic like "!view.abc")
   */
  private async checkPermission(permission: string): Promise<boolean> {
    const isNegated = permission.startsWith("!");
    const permName = isNegated ? permission.substring(1) : permission;

    try {
      const hasPerm = await this.permissionsService
        .hasPermission(permName)
        .then((res: any) => {
          return res;
        })
        .catch(() => false);

      return isNegated ? !hasPerm : hasPerm;
    } catch {
      return isNegated ? true : false;
    }
  }

  /**
   * Check role with specific permissions
   * Example: { 'vendor': ['view.abc', '!edit.xyz'] }
   */
  private async checkRoleWithPermissions(
    role: string,
    permissions: string[]
  ): Promise<boolean> {
    const isRoleNegated = role.startsWith("!");
    const roleName = isRoleNegated ? role.substring(1) : role;

    // First check if user has the role
    const hasRole = await this.permissionsService
      .hasPermission(roleName)
      .then((res) => res)
      .catch(() => false);

    const roleCheck = isRoleNegated ? !hasRole : hasRole;

    // If role check fails, return false
    if (!roleCheck) return false;

    // Check all permissions for this role
    const permissionChecks = await Promise.all(
      permissions.map((perm) => this.checkPermission(perm))
    );

    // All permissions must pass (AND logic within a role)
    return permissionChecks.every((check) => check === true);
  }

  /**
   * Get all user's current roles
   */
  private async getUserRoles(): Promise<string[]> {
    const permissions = this.permissionsService.getPermissions();
    return Object.keys(permissions);
  }

  /**
   * Check if user has ONLY the allowed roles (no extra roles)
   */
  private async checkOnlyRoles(allowedRoles: string[]): Promise<boolean> {
    const userRoles = await this.getUserRoles();

    // User must have at least one allowed role
    const hasAllowedRole = allowedRoles.some((role) =>
      userRoles.includes(role)
    );

    if (!hasAllowedRole) return false;

    // User must not have any roles outside the allowed list
    const hasOnlyAllowedRoles = userRoles.every((userRole) =>
      allowedRoles.includes(userRole)
    );

    return hasOnlyAllowedRoles;
  }

  /**
   * Main access logic - supports:
   * 1. Simple roles: ['support_group', '!vendor']
   * 2. Role with permissions: { 'vendor': ['view.abc'], 'support': ['!view.xyz'] }
   *    For object format: Users with specified roles must have the permissions,
   *    all other users are allowed by default (unless only=true)
   * 3. only mode: When only=true, user must ONLY have the specified roles
   */
  private async checkAccess(): Promise<boolean> {
    const config: any = this.appHasAccess;

    if (!config?.roles) {
      return false;
    }

    if (config?.allowModules && config.allowModules.length > 0) {
      try {
        const permissionStr = localStorage.getItem("permission");
        if (!permissionStr) {
          return false;
        }

        const permissionObj = JSON.parse(permissionStr);

        const isSuperUser = permissionObj?.is_superuser;

        const accessibleModules: string[] =
          permissionObj?.accessible_modules ?? [];

        const ownerEntityId = permissionObj?.owner_entity_id;

        const isOwner = Array.isArray(ownerEntityId)
          ? ownerEntityId.length > 0
          : ownerEntityId !== null &&
          ownerEntityId !== undefined &&
          Number.isInteger(Number(ownerEntityId));

        const hasNegatedModule = config.allowModules.some(
          (m: string) => m.startsWith("!")
        );

        // Super users have access to every module & should never see
        // app-no-license/app-access-denied components.
        if (isSuperUser) {
          return !hasNegatedModule;
        }

        const moduleAccess = config.allowModules.some((m: string) => {
          const negated = m.startsWith("!");
          const module = negated ? m.substring(1) : m;
          const hasModule = accessibleModules.includes(module);

          return negated ? !hasModule : hasModule;
        });

        // Positive module check:
        // - Owners always pass.
        // - Users with the module pass.
        if (!hasNegatedModule) {
          return isOwner || moduleAccess;
        }

        // Negative module check:
        // - Owners should never see app-no-license/app-access-denied.
        // - Only non-owners without the module should pass.
        return !isOwner && moduleAccess;
      } catch (e) {
        console.error("Error checking module permissions", e);
        return false;
      }
    }

    const requireAll = config.requireAll ?? false;
    const onlyMode = config.only ?? false;

    // Case 1: Simple array of roles
    if (Array.isArray(config.roles)) {
      // In "only" mode, check that user has ONLY these roles
      if (onlyMode) {
        // Filter out negated roles for "only" check
        const positiveRoles = config.roles
          .filter((r: string) => !r.startsWith("!"))
          .map((r: string) => r);

        if (positiveRoles.length === 0) return false;

        return this.checkOnlyRoles(positiveRoles);
      }

      // Normal mode: check role access
      const roleChecks = await Promise.all(
        config.roles.map((r: any) => this.checkRole(r))
      );
      return requireAll
        ? roleChecks.every((c: any) => c === true)
        : roleChecks.some((c: any) => c === true);
    }

    // Case 2: Single string role
    if (typeof config.roles === "string") {
      if (onlyMode) {
        const roleName = config.roles.startsWith("!")
          ? config.roles.substring(1)
          : config.roles;
        return this.checkOnlyRoles([roleName]);
      }
      return this.checkRole(config.roles);
    }

    // Case 3: Object with role:permissions mapping
    if (typeof config.roles === "object" && !Array.isArray(config.roles)) {
      const userRoles = await this.getUserRoles();
      const definedRoles = Object.keys(config.roles);
      // In "only" mode, user must have ONLY the defined roles
      if (onlyMode) {
        const hasOnlyDefinedRoles = userRoles.every((userRole) =>
          definedRoles.includes(userRole)
        );
        if (!hasOnlyDefinedRoles) return false;
      }

      // Check if user has any of the restricted roles
      const userHasRestrictedRole = definedRoles.some((role) =>
        userRoles.includes(role)
      );

      // If user doesn't have any of the restricted roles
      if (!userHasRestrictedRole) {
        // If they don't have the required role to satisfy this object mapping, deny access.
        return false;
      }

      // User has at least one restricted role, check permissions
      const checks = await Promise.all(
        definedRoles.map(async (role) => {
          const hasRole = userRoles.includes(role);
          if (!hasRole) return true; // Skip roles user doesn't have

          const permissions = config.roles[role];
          return this.checkRoleWithPermissions(role, permissions);
        })
      );

      // Filter out roles user doesn't have, then check remaining
      const relevantChecks = checks.filter((_, idx) =>
        userRoles.includes(definedRoles[idx])
      );

      return requireAll
        ? relevantChecks.every((c) => c === true)
        : relevantChecks.some((c) => c === true);
    }

    return false;
  }

  /**
   * Update DOM based on access check
   */
  private async updateView() {
    const allowed = await this.checkAccess();
    this.viewContainer.clear();
    if (allowed) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    }
  }
}
